import { GridFilterItem, GridFilterModel } from '@warehouse/object-browser/core';
import {
	BrokerService,
	TENANT_JOB_SEARCH_STATUSES,
	TenantJobSearchFilters,
	TenantJobSearchStatus,
} from '@warehouse/object-browser/feature-broker-core';
import { ServiceColumnName, ServiceLogColumnField, StatusColumnName, TriggeredByColumnName } from './service-log.model';
import { intersectPrefixes } from './service-log-search';

/**
 * Turns the MUI X filter model kept in the table-manager store into the Broker search filters.
 * The Broker only ANDs conditions, so the grid's logic operator is ignored (always AND).
 * Columns without a Broker counterpart (start, end, duration) are ignored.
 */
export const ServiceLogFilterAdapter = {
	toTenantJobSearchFilters(
		filter: GridFilterModel<ServiceLogColumnField>,
		services: BrokerService[],
	): TenantJobSearchFilters {
		const result: TenantJobSearchFilters = {};

		filter.items.forEach((item) => {
			const values = valuesOf(item);
			if (values.length === 0) return;

			switch (item.field) {
				case ServiceColumnName: {
					const named = values
						.map((name) => services.find((service) => service.serviceName === name)?.serviceUUID)
						.filter((uuid): uuid is string => !!uuid);
					if (NEGATIVE_OPERATORS.includes(item.operator)) {
						// Excluded on the Broker side rather than "every other catalog service": jobs of services missing
						// from the catalog are not the excluded one, so they stay.
						result.excludedServiceUUIDs = Array.from(new Set([...(result.excludedServiceUUIDs ?? []), ...named]));
					} else {
						result.serviceUUIDs = narrow(result.serviceUUIDs, named);
					}
					break;
				}
				case StatusColumnName: {
					const named = values.filter((value): value is TenantJobSearchStatus =>
						(TENANT_JOB_SEARCH_STATUSES as string[]).includes(value),
					);
					const next = NEGATIVE_OPERATORS.includes(item.operator)
						? TENANT_JOB_SEARCH_STATUSES.filter((status) => !named.includes(status))
						: named;
					result.statuses = narrow(result.statuses, next);
					break;
				}
				case TriggeredByColumnName: {
					// Keyword field on the Broker: exact match or prefix only; any other operator cannot be honoured and is ignored.
					// Two conditions on the column are ANDed like everywhere else, never concatenated (the Broker ORs a list).
					const current = result.triggeredByDisplayName ?? {};
					if (item.operator === 'startsWith') {
						result.triggeredByDisplayName = { ...current, startsWith: intersectPrefixes(current.startsWith, values) };
					} else if (item.operator === 'equals' || item.operator === 'isAnyOf') {
						result.triggeredByDisplayName = { ...current, equals: narrow(current.equals, values) };
					}
					break;
				}
				default:
					break;
			}
		});

		return result;
	},
};

/**
 * Negative operators: a service is excluded on the Broker side (`doesNotEqual`); a status, a closed set, becomes every
 * other status. A positive operator naming nothing known (a service since removed from the catalog) matches no job,
 * so an active filter never silently lists everything.
 */
const NEGATIVE_OPERATORS = ['not', 'isNoneOf'];

/** Conditions on the same column are ANDed, like everything else: the allowed values are intersected. */
function narrow<T extends string>(current: T[] | undefined, next: T[]): T[] {
	return current ? current.filter((value) => next.includes(value)) : next;
}

function valuesOf(item: GridFilterItem<ServiceLogColumnField>): string[] {
	if (item.value === undefined || item.value === null || item.value === '') return [];
	const raw: unknown[] = Array.isArray(item.value) ? item.value : [item.value];
	return raw.map((value) => String(value)).filter((value) => value.length > 0);
}
